import React from "react";
import {
  Box,
  InputAdornment,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import { formatMonetaryValue } from "../utils/MonetaryFormatHelper";

interface Props {
  amountPaid: string;
  onChange: (value: string) => void;
  claimStatus: string;
}

const AmountAlreadyPaid: React.FC<Props> = ({
  amountPaid,
  onChange,
  claimStatus,
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [localValue, setLocalValue] = React.useState(amountPaid);

  React.useEffect(() => {
    if (!isFocused) {
      const formatted = amountPaid ? formatMonetaryValue(amountPaid) : amountPaid;
      setLocalValue(formatted);
    }
  }, [amountPaid, isFocused]);

  const getDisplayValue = () => {
    if (isFocused) {
      return localValue;
    }
    return amountPaid ? formatMonetaryValue(amountPaid) : amountPaid;
  };

  const displayValue = getDisplayValue();

  return (
    <Box
      sx={{
        backgroundColor: "white",
        padding: "20px",
        height: "250px",
      }}
    >
      <Typography
        sx={{
          color: "#707070",
          fontWeight: 700,
          fontSize: "14px",
          lineHeight: "26px",
        }}
      >
        Amount Already Paid
      </Typography>
      <p
        style={{
          color: "#707070",
          fontSize: "12px",
          lineHeight: "16px",
          marginBlockStart: "0px",
          marginBlockEnd: "0px",
          marginBottom: "15px",
          marginTop: "5px",
          fontWeight: 400,
        }}
      >
        This is the total of amounts already collected from the patient or other
        payers. <b>This is uncommon.</b>
      </p>

      <InputLabel
        htmlFor="amountPaid"
        sx={{
          color: "#A29D9B",
          fontWeight: 700,
          fontSize: "12px",
          lineHeight: "18px",
          marginBottom: "5px",
        }}
      >
        Amount Paid
      </InputLabel>

      <Box className="field-container" sx={{ marginLeft: "0px" }}>
        <TextField
          id="amountPaid"
          size="small"
          value={displayValue}
          className="the-field"
          required
          variant="outlined"
          placeholder="####.##"
          autoComplete="off"
          disabled={
            claimStatus === "submitted" || claimStatus === "pending_submission"
          }
          onFocus={() => {
            setIsFocused(true);
            setLocalValue(amountPaid || '');
          }}
          onChange={(e) => {
            const value = e.target.value;
            if (/^\d*\.?\d{0,2}$/.test(value)) {
              setLocalValue(value);
              onChange(value);
            }
          }}
          onBlur={(e) => {
            setIsFocused(false);
            const formatted = formatMonetaryValue(e.target.value);
            if (formatted !== '') {
              onChange(formatted);
            } else if (e.target.value === '') {
              onChange('');
            }
          }}
          sx={{
            width: "100% !important",
            "& .MuiInputBase-input": {
              paddingLeft: amountPaid && "0px !important",
            },
            "& .MuiOutlinedInput-root": {
              paddingLeft: 0,
            },
          }}
          slotProps={{
            input: {
              inputMode: "numeric",
              startAdornment: amountPaid ? (
                <InputAdornment
                  position="start"
                  sx={{ marginRight: "0px", paddingLeft: "10.5px" }}
                >
                  <Typography
                    sx={{
                      color:
                        claimStatus === "submitted" ||
                        claimStatus === "pending_submission"
                          ? "rgba(0, 0, 0, 0.38)"
                          : "#4A4442",
                      fontSize: "14px",
                    }}
                  >
                    $
                  </Typography>
                </InputAdornment>
              ) : undefined,
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default AmountAlreadyPaid;
